/**
 * ModalContext - Gestione centralizzata dei modali
 */

import React, { createContext, useContext, useState, useCallback } from 'react';

const ModalContext = createContext();

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal deve essere usato all\'interno di ModalProvider');
  }
  return context;
};

export const ModalProvider = ({ children }) => {
  const [modals, setModals] = useState([]);

  /**
   * Apre un nuovo modale
   * @param {Object} modalConfig - Configurazione del modale
   * @param {string} modalConfig.id - ID univoco del modale
   * @param {React.Component} modalConfig.component - Componente da renderizzare
   * @param {Object} modalConfig.props - Props da passare al componente
   * @param {Object} modalConfig.options - Opzioni del modale (size, closable, etc.)
   */
  const openModal = useCallback((modalConfig) => {
    const { id, component, props = {}, options = {} } = modalConfig;

    setModals((prev) => {
      // Evita duplicati
      if (prev.some((modal) => modal.id === id)) {
        console.warn(`Modale con id "${id}" già aperto`);
        return prev;
      }

      return [
        ...prev,
        {
          id,
          component,
          props,
          options: {
            size: 'medium', // small, medium, large, fullscreen
            closable: true,
            closeOnOverlayClick: true,
            showCloseButton: true,
            ...options,
          },
        },
      ];
    });
  }, []);

  /**
   * Chiude un modale specifico
   * @param {string} id - ID del modale da chiudere
   */
  const closeModal = useCallback((id) => {
    setModals((prev) => prev.filter((modal) => modal.id !== id));
  }, []);

  /**
   * Chiude tutti i modali
   */
  const closeAllModals = useCallback(() => {
    setModals([]);
  }, []);

  /**
   * Verifica se un modale è aperto
   * @param {string} id - ID del modale
   * @returns {boolean}
   */
  const isModalOpen = useCallback(
    (id) => {
      return modals.some((modal) => modal.id === id);
    },
    [modals]
  );

  /**
   * Reset completo dello stato dei modali
   * Da chiamare al logout per pulizia completa
   */
  const resetModalState = useCallback(() => {
    console.log('[ModalContext] Reset completo stato modali');
    setModals([]);
  }, []);

  const value = {
    modals,
    openModal,
    closeModal,
    closeAllModals,
    isModalOpen,
    resetModalState,
  };

  return <ModalContext.Provider value={value}>{children}</ModalContext.Provider>;
};
